import { Base } from '@studiometa/js-toolkit';

const STORAGE_KEY = 'maintenance-overlay-dismissed';
const CHANNEL_NAME = 'maintenance-overlay';

/**
 * MaintenanceOverlay class.
 */
export default class MaintenanceOverlay extends Base {
  /**
   * Class config.
   * @type {object}
   */
  static config = {
    name: 'MaintenanceOverlay',
    refs: ['dismiss'],
  };

  /**
   * Initialize the overlay state and listen for cross-tab dismissals.
   * @returns {void}
   */
  mounted() {
    this.channel =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

    if (this.channel) {
      this.channel.addEventListener('message', this.onChannelMessage.bind(this));
    }

    if (this.isDismissedLocally()) {
      this.hideOverlay();
      return;
    }

    if (this.channel) {
      this.channel.postMessage('query');
    }
  }

  /**
   * Close the BroadcastChannel when the component is destroyed.
   * @returns {void}
   */
  destroyed() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }

  /**
   * Reply with the local dismissal state or apply a peer's dismissal.
   * @param   {MessageEvent} event Incoming BroadcastChannel message.
   * @returns {void}
   */
  onChannelMessage(event) {
    if (event.data === 'query' && this.isDismissedLocally()) {
      this.channel.postMessage('dismissed');
    } else if (event.data === 'dismissed' && !this.isDismissedLocally()) {
      this.markDismissed();
      this.hideOverlay();
    }
  }

  /**
   * Handle a click on the CTA — persist the dismissal and notify peers.
   * @returns {void}
   */
  onDismissClick() {
    this.markDismissed();
    if (this.channel) {
      this.channel.postMessage('dismissed');
    }
    this.hideOverlay();
  }

  /**
   * Whether the overlay has already been dismissed in this tab.
   * @returns {boolean}
   */
  isDismissedLocally() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Persist the dismissal in sessionStorage and on the documentElement.
   * @returns {void}
   */
  markDismissed() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // sessionStorage unavailable (private mode / quota); proceed with DOM dismissal only.
    }
    document.documentElement.classList.add('maintenance-dismissed');
  }

  /**
   * Remove the overlay element from the DOM.
   * @returns {void}
   */
  hideOverlay() {
    if (this.$el && this.$el.parentNode) {
      this.$el.remove();
    }
  }
}
